/* ---------------------------------------------------------------------------
   Signing in.

   THE REGISTER'S OWN LOGIN. It was Cloudflare Access until the Department asked
   for sign-in it could administer without a Cloudflare account — adding a
   colleague cannot require an account belonging to somebody outside the
   Division. So this screen is now a username and a password, checked against
   `users` by functions/api/auth/login.ts.

   WHAT THAT COST, RECORDED HERE BECAUSE THIS IS THE SCREEN IT SHOWS UP ON: SSO,
   MFA, and a refusal that used to happen at Cloudflare's edge before any code
   here ran. This form is now the thing between the public internet and a
   government asset register. Everything visible on it — one message for every
   kind of failure, no hint about which usernames exist, a lockout that counts —
   follows from that.

   WHILE ACCESS IS STILL IN FRONT, this screen is nearly unreachable: the edge
   authenticates before the application loads. The two coexist deliberately
   during the switch, so the password login can be deployed and proven before
   Access comes off rather than after.

   WHAT IT REPLACED. An expired Access session surfaced as a failed hydrate, which
   `hydrate` treats like any other unreachable backend, whose fallback is the
   bundled seed. So somebody whose session had lapsed was shown a register of five
   structures, drawn normally, sortable, with the real reason available only in the
   tooltip of the connection pill — and every write they attempted queued onto the
   device against a session that no longer existed. Third instance of the same
   failure in this codebase and the same rule settles all three: "we cannot ask"
   and "here is the answer" are different claims, and only one of them is safe to
   draw. See `Not screened` in the hazard screening, and src/screens/suspended.ts.

   IT REPLACES THE WHOLE SHELL, not the screen inside it. A title bar with a
   search box, a nav pane with eight destinations and a register count of "5 of 5"
   drawn around a sign-in notice would be the same lie in a smaller box: none of
   those controls can do anything until the session is back. So src/main.ts draws
   this instead of everything — see `showsSignIn`.

   THE QUEUE IS THE PART THAT MATTERS. An inspector whose session lapsed in a
   valley may be holding a day of work on the device. This screen counts it, says
   it is still there, and says the one thing somebody about to panic needs to
   read: signing in again sends it. Nothing here clears anything.

   IT CANNOT APPEAR FOR A DEVICE WITH NO SIGNAL. `session` is set only by the
   server answering `reason: 'access'` — never by a request that failed — because
   signing in is the one thing you cannot do with no connection, and a sign-in
   screen in a gorge would be an application that had locked out the person it was
   written for. See the note on `session` in src/core/store.ts.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS, type Child } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { button, card, cardHead, fact, field } from '../components/widgets.ts';
import { agency, brand } from '../tenant/agency.ts';

/**
 * Whether the shell is replaced by this screen.
 *
 * A FUNCTION, AND EXPORTED, for the reason this codebase keeps relearning: the
 * rule belongs somewhere a check can call it, not inside src/main.ts's
 * dispatcher, which the smoke suite cannot reach — it composes screens through
 * its own renderer map. Same fix as `routeFor` in src/core/router.ts and
 * `refusesRegister` in src/screens/suspended.ts.
 *
 * NO SCREEN IS EXEMPT, which is the one difference from `refusesRegister`. That
 * rule spares the guide and Options, because a suspended reader is still signed
 * in and both of those still work for them. Here nothing works: Options reads
 * this device's queue and the register's identity, and the guide — while it would
 * render — would be the application answering a question nobody asked while the
 * one thing they need to do sits behind a button they cannot see. The way to the
 * guide is signing in.
 */
export function showsSignIn(s: Readonly<State>): boolean {
  return s.session === 'lost';
}

/**
 * The page both screens share: the department's panel, and a column for the form.
 *
 * The panel is the department's own colours and emblems, from the tenant profile
 * — see `TenantBrand` in src/tenant/types.ts. A department with no
 * emblems gets the bridge mark in their place, not an empty row.
 */
function signInPage(...content: Child[]): HTMLElement {
  const a = agency();
  const b = brand();
  return h(
    'div',
    { class: 'signin', style: { '--signin-ground': b.ground } },
    h(
      'section',
      { class: 'signin__brand' },
      h(
        'div',
        { class: 'signin__stripe', 'aria-hidden': 'true' },
        ...b.stripe.map((c) => h('span', { style: { background: c } })),
      ),
      h(
        'header',
        { class: 'signin__agency' },
        b.emblems.length
          ? b.emblems.map((e) =>
              h(
                'span',
                { class: ['signin__emblem', e.disc && 'signin__emblem--disc'] },
                h('img', { src: e.src, alt: e.alt }),
              ),
            )
          : h('span', { class: 'signin__mark' }, icon(ICONS.bridge, { size: 30, stroke: 'currentColor' })),
        h('span', { class: 'signin__rule', 'aria-hidden': 'true' }),
        h(
          'div',
          { class: 'signin__names' },
          h('span', { class: 'signin__gov', text: a.government }),
          h('span', { class: 'signin__dept', text: a.department }),
        ),
      ),
      h(
        'div',
        { class: 'signin__hero' },
        h('span', { class: 'signin__branch', text: a.branch }),
        h('h1', { class: 'signin__title', text: 'Bridge Watch' }),
      ),
      h(
        'footer',
        { class: 'signin__foot' },
        h('span', { text: `${a.departmentShort} · ${a.branch}` }),
        h('span', { text: a.unitShort }),
      ),
    ),
    h('main', { class: 'signin__main' }, h('div', { class: 'signin__panel' }, ...content)),
  );
}

export function signInScreen(s: Readonly<State>): HTMLElement {
  const queued = s.queued.length;

  return signInPage(
      card(
        { pad: true, class: 'signin__card' },
        h('h2', { class: 'signin__head', text: 'Sign in' }),

        /* The register's own words about why this screen appeared, where it had
           something to say. An expired session and a browser that was never
           signed in read differently, and the endpoint already distinguishes
           them — so it is shown rather than paraphrased into one sentence that
           fits neither. */
        s.sessionNote
          ? h('p', { class: 'signin__lead', text: s.sessionNote })
          : h('p', {
              class: 'signin__lead',
              text: 'Sign in to reach the Department’s bridge register.',
            }),

        h(
          'form',
          {
            class: 'signin__form',
            /* A real form, so a password manager offers to fill it and Enter
               submits from either box. Both are things people expect of a login
               and neither survives a div with two inputs in it. */
            onsubmit: (e: Event) => {
              e.preventDefault();
              void actions.submitSignIn();
            },
          },
          field(
            'Username',
            h('input', {
              id: 'signin-user',
              class: 'input',
              type: 'text',
              value: s.signInUser,
              autocomplete: 'username',
              autocapitalize: 'none',
              spellcheck: 'false',
              /* Autofocus on the first box: this screen exists to be typed into,
                 and it is the only thing on it. */
              autofocus: 'autofocus',
              oninput: (e: Event) =>
                actions.setSignInField('signInUser', (e.target as HTMLInputElement).value),
            }),
            'signin-user',
          ),
          field(
            'Password',
            h(
              'div',
              { class: 'signin__secret' },
              h('input', {
                id: 'signin-password',
                class: 'input',
                type: s.signInReveal ? 'text' : 'password',
                value: s.signInPassword,
                autocomplete: 'current-password',
                oninput: (e: Event) =>
                  actions.setSignInField('signInPassword', (e.target as HTMLInputElement).value),
              }),
              h(
                'button',
                {
                  type: 'button',
                  class: 'signin__reveal',
                  'aria-label': s.signInReveal ? 'Hide password' : 'Show password',
                  'aria-pressed': s.signInReveal ? 'true' : 'false',
                  onclick: () => actions.toggleSignInReveal(),
                },
                icon(s.signInReveal ? ICONS.eyeOff : ICONS.eye, { size: 18, width: 1.8 }),
              ),
            ),
            'signin-password',
          ),

          /* ONE MESSAGE FOR EVERY KIND OF FAILURE, and it comes from the server.
             A username nobody has and a password that is wrong say exactly the
             same thing, because the two of them apart are a list of everybody in
             the Division. A lockout says something different, and may: you only
             reach it by having already guessed at that account five times. */
          s.signInError ? signInError(s.signInError) : null,

          h(
            'div',
            { class: 'signin__actions' },
            button(s.signInBusy ? 'Signing in…' : 'Sign in', {
              primary: true,
              block: true,
              tall: true,
              type: 'submit',
              disabled: s.signInBusy,
            }),
          ),
        ),
      ),

      /* WHAT IS STILL ON THIS DEVICE. The most important thing on the screen for
         the one reader most likely to see it: an inspector whose session lapsed
         somewhere with no signal, who has a day of work on a phone and no way to
         tell whether it survived. It did. Nothing on this screen clears
         anything. */
      queued
        ? card(
            { pad: true, class: 'signin__queued' },
            cardHead('Waiting on this device'),
            fact(
              'Not yet in the register',
              `${queued} ${queued === 1 ? 'entry' : 'entries'}`,
            ),
            h('p', {
              class: 'signin__note',
              text: 'Sent once you are signed in again. Resetting offline storage would lose them.',
            }),
          )
        : null,
  );
}

function signInError(text: string): HTMLElement {
  return h(
    'p',
    { class: 'signin__error', role: 'alert' },
    icon(ICONS.incident, { size: 16, width: 2 }),
    h('span', { text }),
  );
}

/* --------------------------- choosing a password --------------------------- */

/**
 * Whether the shell is replaced by the change-password screen.
 *
 * TRUE ONLY FOR A TEMPORARY PASSWORD, which is a different situation from a lost
 * session and must not be confused with one: this browser IS signed in, and the
 * register is waiting for one specific act. Showing the sign-in screen here
 * would ask somebody to do the thing they have just done.
 *
 * It is checked after `showsSignIn` in src/main.ts, because a session that has
 * gone beats a password that needs changing — there is nothing to change it
 * from.
 */
export function showsPasswordChange(s: Readonly<State>): boolean {
  return s.session === 'ok' && s.mustChangePassword;
}

/**
 * Choose a password, when the register will not let you do anything else.
 *
 * REACHED WITH A PASSWORD SOMEBODY ELSE CHOSE — an administrator generated it
 * and read it out — so it is a credential that has been spoken aloud, possibly
 * written down, and is known to at least two people. That is the entire reason
 * this screen takes over: the server refuses every other route for a session
 * carrying `must_change` (see functions/lib/sessionGuard.ts), and this makes the
 * client agree rather than leaving a person confused about why nothing works.
 *
 * IT ASKS FOR THE TEMPORARY ONE AGAIN. A session cookie is a bearer credential;
 * requiring the current password means a borrowed session cannot become a
 * permanent takeover of the account.
 */
export function passwordChangeScreen(s: Readonly<State>): HTMLElement {
  return signInPage(
      card(
        { pad: true, class: 'signin__card' },
        h('h2', { class: 'signin__head', text: 'Choose a password' }),
        h('p', {
          class: 'signin__lead',
          text:
            'You are signed in with a password an administrator issued. Somebody else chose it ' +
            'and it was read out to you, so it is not yours yet — choose one only you know, and ' +
            'the register will let you get on.',
        }),

        h(
          'form',
          {
            class: 'signin__form',
            onsubmit: (e: Event) => {
              e.preventDefault();
              void actions.submitPasswordChange();
            },
          },
          field(
            'The password you were given',
            h('input', {
              id: 'pw-current',
              class: 'input',
              type: 'password',
              value: s.passwordCurrent,
              autocomplete: 'current-password',
              oninput: (e: Event) =>
                actions.setPasswordField('passwordCurrent', (e.target as HTMLInputElement).value),
            }),
            'pw-current',
          ),
          field(
            'Your new password',
            h('input', {
              id: 'pw-next',
              class: 'input',
              type: 'password',
              value: s.passwordNext,
              autocomplete: 'new-password',
              oninput: (e: Event) =>
                actions.setPasswordField('passwordNext', (e.target as HTMLInputElement).value),
            }),
            'pw-next',
          ),

          s.passwordError ? signInError(s.passwordError) : null,

          h(
            'div',
            { class: 'signin__actions' },
            button(s.passwordBusy ? 'Saving…' : 'Save it and continue', {
              primary: true,
              block: true,
              tall: true,
              type: 'submit',
              disabled: s.passwordBusy,
            }),
          ),
        ),

        /* LENGTH, AND NOT A LIST OF RULES. Composition requirements produce
           `Password1!` and a sticky note; NIST has advised against them since SP
           800-63B. */
        h('p', {
          class: 'signin__note',
          text: 'At least twelve characters, not containing your username.',
        }),
      ),

      /* The way out for somebody who has arrived here on the wrong account — a
         shared office tablet, most likely. Signing out is the one other thing
         the server permits with a temporary password. */
      h(
        'div',
        { class: 'signin__actions' },
        button('Sign out instead', {
          block: true,
          tall: true,
          onClick: () => void actions.signOut(),
        }),
      ),
  );
}
